import { useMemo, useState } from 'react';
import {
  isQuickFillSessionUndoSnapshotCompatible,
  useQuickFillStore,
} from '../../../shared/stores/useQuickFillStore';
import { useSnackbarStore } from '../../../shared/stores/useSnackbarStore';
import type { TableInitialPreset } from '../../../shared/ui/table';
import {
  useCellSelection,
  useQuickFillConfigSync,
  useQuickFillDataBuilder,
  useQuickFillInitialization,
  useQuickFillKeyLibrary,
  useQuickFillModelStatus,
  useQuickFillNavigation,
  useQuickFillSubmit,
  useQuickFillTableOperations,
  useQuickFillValidationFocus,
} from '.';
import {
  buildQuickFillParameterLabelMap,
  filterQuickFillPropertiesByAvailableParameters,
  getQuickFillDataRowModes,
  resolveQuickFillNameColumnLabel,
} from '../lib/quickFillPageModel';
import {
  normalizeQuickFillLabel,
  remapValidationQuickFillFocusColumnLabels,
} from '../lib/quickFillLabelNormalization';
import type { QuickFillLoadProgress } from '../lib/quickFillLoadProgress';
import type { QuickFillPageConnectorController } from './useQuickFillPageConnectorController';
import { useQuickFillCompleteCatalog } from './useQuickFillCompleteCatalog';
import { useActiveDocumentRefresh } from '../../../shared/hooks/ActiveDocumentProvider';

export function useQuickFillPageWorkspaceController(
  connector: QuickFillPageConnectorController,
  onContinue: () => void
) {
  const properties = useQuickFillStore((state) => state.properties);
  const extraProperties = useQuickFillStore((state) => state.extraProperties);
  const quickFillData = useQuickFillStore((state) => state.quickFillData);
  const editedCells = useQuickFillStore((state) => state.editedCells);
  const quickFillHasPendingChanges = useQuickFillStore((state) => state.hasPendingChanges);
  const sessionUndoSnapshots = useQuickFillStore((state) => state.sessionUndoSnapshots);
  const loading = useQuickFillStore((state) => state.isLoading);
  const combinedError = useQuickFillStore((state) => state.error);
  const updatePropertyChecked = useQuickFillStore((state) => state.updatePropertyChecked);
  const updatePropertyRevitParameter = useQuickFillStore(
    (state) => state.updatePropertyRevitParameter
  );
  const addExtraProperty = useQuickFillStore((state) => state.addExtraProperty);
  const removeExtraProperty = useQuickFillStore((state) => state.removeExtraProperty);
  const updateExtraPropertyChecked = useQuickFillStore(
    (state) => state.updateExtraPropertyChecked
  );
  const restoreSessionUndoSnapshot = useQuickFillStore(
    (state) => state.restoreSessionUndoSnapshot
  );
  const clearSessionUndoSnapshots = useQuickFillStore(
    (state) => state.clearSessionUndoSnapshots
  );
  const snackbar = useSnackbarStore();
  const { modelDataStale, refreshModelData } = useActiveDocumentRefresh();
  const [validationFocusPreset, setValidationFocusPreset] =
    useState<TableInitialPreset | null>(null);
  const [propertyPanelOpen, setPropertyPanelOpen] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window === 'undefined' ? 1280 : window.innerWidth
  );
  const [isUndoingQuickFillSessions, setIsUndoingQuickFillSessions] = useState(false);
  const operations = useQuickFillTableOperations(connector.quickFillParameterBindingKinds);
  const keyLibrary = useQuickFillKeyLibrary({
    quickFillData,
    selectedRows: operations.selectedRows,
  });
  const cellSelection = useCellSelection({ sameColumnOnly: true });
  const safeProperties = useMemo(() => (Array.isArray(properties) ? properties : []).map(
    (property) => ({ ...property, isReadOnly: Boolean((property as any).isReadOnly) })
  ), [properties]);
  const configuredVisibleProperties = useMemo(() => [
    ...safeProperties.filter((property) => property?.checked),
    ...(Array.isArray(extraProperties) ? extraProperties : []).filter(
      (property) => property?.checked
    ),
  ], [extraProperties, safeProperties]);
  const configuredVisiblePropertyLabels = useMemo(
    () => configuredVisibleProperties.map((property) => property.label),
    [configuredVisibleProperties]
  );
  const navigation = useQuickFillNavigation({
    quickFillData,
    selectedCells: cellSelection.selectedCells,
    selectedRows: operations.selectedRows,
    visiblePropertyLabels: configuredVisiblePropertyLabels,
    initialCollapsed: connector.useDrawerPanels,
  });
  const validationFocus = useQuickFillValidationFocus({
    connectorDefaultRowMode: connector.connectorDefaultRowMode,
    setRowMode: connector.setRowMode,
    manualRowModeOverrideRef: connector.manualRowModeOverrideRef,
    consumedValidationFocusRequestIdRef: connector.consumedValidationFocusRequestIdRef,
    lastValidationFocusWaitStateRef: connector.lastValidationFocusWaitStateRef,
    replaceSelectedRows: operations.replaceSelectedRows,
    setNavigationMarkerRows: navigation.setNavigationMarkerRows,
    setNavigationRequest: navigation.setNavigationRequest,
    setValidationFocusPreset,
  });
  const initialization = useQuickFillInitialization(
    validationFocus.effectiveValidationScope,
    connector.quickFillSnapshotShape,
    connector.quickFillCacheScopeKey,
    operations.isSyncing || connector.parameterAddition.isAdding,
    connector.quickFillRequestedParameterIntents
  );
  const availableParameters = useQuickFillCompleteCatalog({
    open: propertyPanelOpen,
    startupParameters: initialization.availableParameters,
    projectKey: connector.activeDocumentInfo.projectKey,
    modelVersion: connector.activeDocumentInfo.modelVersion,
    mutationActive: operations.isSyncing || connector.parameterAddition.isAdding,
    onWarning: snackbar.showWarning,
  });
  const availableProperties = useMemo(() => filterQuickFillPropertiesByAvailableParameters(
    safeProperties,
    availableParameters,
    initialization.isRevitDataLoaded
  ), [availableParameters, initialization.isRevitDataLoaded, safeProperties]);
  const availableExtraProperties = useMemo(() => filterQuickFillPropertiesByAvailableParameters(
    Array.isArray(extraProperties) ? extraProperties : [],
    availableParameters,
    initialization.isRevitDataLoaded
  ), [availableParameters, extraProperties, initialization.isRevitDataLoaded]);
  const visibleProperties = useMemo(() => [
    ...availableProperties.filter((property) => property?.checked),
    ...availableExtraProperties.filter((property) => property?.checked),
  ], [availableExtraProperties, availableProperties]);
  const visiblePropertyLabels = useMemo(
    () => visibleProperties.map((property) => property.label),
    [visibleProperties]
  );
  const allowFamilyTypeFallback = connector.quickFillSnapshotShape === 'familyType';
  const shouldUseFamilyTypeFallback = allowFamilyTypeFallback &&
    connector.rowMode === 'instance' &&
    initialization.isRevitDataLoaded &&
    initialization.pipeElements.length === 0 &&
    initialization.pipeFamilies.length > 0;
  const shouldUseFamilyTypeFallbackForValidation = shouldUseFamilyTypeFallback &&
    Boolean(validationFocus.effectiveValidationScope?.requestId) &&
    (validationFocus.effectiveValidationScope?.preferredRowMode ?? 'familyType') === 'instance';
  const effectiveRowMode = shouldUseFamilyTypeFallback
    ? 'familyType' as const
    : connector.rowMode;
  const nameColumnLabel = resolveQuickFillNameColumnLabel(effectiveRowMode);
  const configSync = useQuickFillConfigSync();
  const builder = useQuickFillDataBuilder(
    initialization.pipeFamilies,
    initialization.pipeElements,
    availableParameters,
    initialization.isInitializing,
    initialization.initializationDoneRef,
    effectiveRowMode,
    allowFamilyTypeFallback,
    connector.quickFillSnapshotShape,
    initialization.quickFillDatasetSourceKey,
    connector.quickFillParameterBindingKinds
  ) || { isBuilding: false, buildProgress: '' };
  const modelStatus = useQuickFillModelStatus({
    projectKey: connector.activeDocumentInfo.projectKey ?? undefined,
  });
  const isDataLoading = initialization.isInitializing || loading ||
    !initialization.isRevitDataLoaded;
  const isTableLoading = isDataLoading || builder.isBuilding;
  const shouldShowTableLoadingOverlay = isDataLoading ||
    (builder.isBuilding && quickFillData.length === 0);
  const showQuickFillApplyProgress = Boolean(operations.isSyncing);
  const applyProgressMessage = operations.syncProgress?.message ||
    (showQuickFillApplyProgress ? 'Preparing Quick Fill changes for Revit' : '');
  const applyProgressTotal = Math.max(0, operations.syncProgress?.total ?? 0);
  const applyProgressCurrent = Math.max(
    0,
    Math.min(operations.syncProgress?.current ?? 0, applyProgressTotal)
  );
  const applyProgressDetail = applyProgressTotal > 0
    ? `${applyProgressCurrent} of ${applyProgressTotal} values confirmed`
    : operations.syncProgress?.detail || 'Waiting for Revit to accept the write request';
  const safeSessionUndoSnapshots = useMemo(
    () => Array.isArray(sessionUndoSnapshots) ? sessionUndoSnapshots : [],
    [sessionUndoSnapshots]
  );
  const compatibleSessionUndoSnapshots = useMemo(
    () => safeSessionUndoSnapshots.filter(
      (snapshot) => isQuickFillSessionUndoSnapshotCompatible(snapshot, quickFillData)
    ),
    [quickFillData, safeSessionUndoSnapshots]
  );
  // Snapshots are stored newest-first and undo restores one session per
  // click, so the target is the most recent compatible snapshot.
  const quickFillSessionUndoTarget = compatibleSessionUndoSnapshots.length > 0
    ? compatibleSessionUndoSnapshots[0]
    : null;
  const quickFillSessionUndoTargetIndex = quickFillSessionUndoTarget
    ? safeSessionUndoSnapshots.findIndex(
      (snapshot) => snapshot.id === quickFillSessionUndoTarget.id
    )
    : -1;
  const parameterToLabel = useMemo(
    () => buildQuickFillParameterLabelMap(
      safeProperties,
      Array.isArray(extraProperties) ? extraProperties : []
    ),
    [extraProperties, safeProperties]
  );
  const submit = useQuickFillSubmit({
    handleSubmit: operations.handleSubmit,
    quickFillData,
    parameterToLabel,
    isTableLoading,
    isQuickFillSyncing: operations.isSyncing,
    onContinue,
  });
  const quickFillColumnProperties = useMemo(() => [
    ...safeProperties,
    ...(Array.isArray(extraProperties) ? extraProperties : []),
  ], [extraProperties, safeProperties]);
  const activeValidationQuickFillFocusForColumns = useMemo(
    () => remapValidationQuickFillFocusColumnLabels(
      validationFocus.activeValidationQuickFillFocus,
      quickFillColumnProperties
    ),
    [quickFillColumnProperties, validationFocus.activeValidationQuickFillFocus]
  );
  const highlightedQuickFillColumnLabels =
    activeValidationQuickFillFocusForColumns?.labels ?? [];
  const activeLoadProgress: QuickFillLoadProgress | null = isTableLoading
    ? builder.isBuilding
      ? {
        phase: 'building',
        message: builder.buildProgress || 'Building Quick Fill rows',
        detail: `Preparing ${effectiveRowMode === 'instance' ? 'instance' : 'family/type'} rows for ${visibleProperties.length} visible columns`,
        currentStep: 4,
        totalSteps: initialization.loadProgress?.totalSteps ?? 4,
        metrics: [
          { label: 'Families', value: String(initialization.pipeFamilies.length) },
          { label: 'Elements', value: String(initialization.pipeElements.length) },
          { label: 'Columns', value: String(visibleProperties.length) },
          { label: 'Mode', value: effectiveRowMode === 'instance' ? 'Instance' : 'Family/type' },
        ],
      }
      : initialization.loadProgress ?? {
        phase: 'requesting',
        message: 'Loading Quick Fill data from Revit',
        detail: 'Waiting for the Revit connector',
        currentStep: 2,
        totalSteps: 4,
      }
    : null;
  const activeLoadProgressText = activeLoadProgress
    ? `${activeLoadProgress.message}${activeLoadProgress.detail ? `. ${activeLoadProgress.detail}` : ''}`
    : '';
  const isApplyDisabled = visibleProperties.length === 0 ||
    isTableLoading || operations.isSyncing || modelStatus.isModelWriteBlocked;
  const isContinueDisabled = isTableLoading || submit.isContinuing ||
    operations.isSyncing || modelStatus.isModelWriteBlocked;
  const hiddenValidationFocusLabels = highlightedQuickFillColumnLabels.filter((label) => {
    const normalized = normalizeQuickFillLabel(label);
    const property = safeProperties.find(
      (item) => normalizeQuickFillLabel(item.label) === normalized
    );
    if (property) return !property.checked;
    const extra = (Array.isArray(extraProperties) ? extraProperties : []).find(
      (item) => normalizeQuickFillLabel(item.label) === normalized
    );
    return extra ? !extra.checked : false;
  });

  return {
    ...connector,
    properties,
    extraProperties,
    quickFillData,
    editedCells,
    quickFillHasPendingChanges,
    updatePropertyChecked,
    updatePropertyRevitParameter,
    addExtraProperty,
    removeExtraProperty,
    updateExtraPropertyChecked,
    restoreSessionUndoSnapshot,
    clearSessionUndoSnapshots,
    snackbar,
    validationFocusPreset,
    setValidationFocusPreset,
    propertyPanelOpen,
    setPropertyPanelOpen,
    viewportWidth,
    setViewportWidth,
    isUndoingQuickFillSessions,
    setIsUndoingQuickFillSessions,
    operations,
    keyLibrary,
    cellSelection,
    safeProperties,
    availableProperties,
    availableExtraProperties,
    visibleProperties,
    visiblePropertyLabels,
    navigation,
    validationFocus,
    initialization: { ...initialization, availableParameters },
    shouldUseFamilyTypeFallbackForValidation,
    shouldUseFamilyTypeFallback,
    effectiveRowMode,
    nameColumnLabel,
    configSync,
    builder,
    modelStatus,
    modelDataStale,
    handleRefreshModelData: refreshModelData,
    isTableLoading,
    shouldShowTableLoadingOverlay,
    showQuickFillApplyProgress,
    applyProgressMessage,
    applyProgressDetail,
    safeSessionUndoSnapshots,
    compatibleSessionUndoSnapshots,
    quickFillSessionUndoTarget,
    quickFillSessionUndoTargetIndex,
    submit,
    highlightedQuickFillColumnLabels,
    activeValidationQuickFillFocusForColumns,
    activeLoadProgress,
    activeLoadProgressText,
    isApplyDisabled,
    isContinueDisabled,
    hiddenValidationFocusLabels,
    combinedError,
    quickFillDataRowModes: getQuickFillDataRowModes(quickFillData),
  };
}

export type QuickFillPageWorkspaceController = ReturnType<
  typeof useQuickFillPageWorkspaceController
>;
